import {
    SignedDocument,
    SignedDocumentResponse,
    SignedDocumentType,
    DocumentStatistics,
    DocumentDownloadResponse,
    DocumentTypesResponse
} from '../types/signedDocument';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class SignedDocumentService {
    private getAuthHeader(): Record<string, string> {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * Получить все подписанные документы компании
     */
    async getSignedDocuments(type?: SignedDocumentType, contractStatus?: string): Promise<SignedDocumentResponse> {
        const params = new URLSearchParams();
        if (type) params.append('type', type);
        if (contractStatus) params.append('contractStatus', contractStatus);

        const queryParam = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(`${API_BASE_URL}/signed-documents${queryParam}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Ошибка получения подписанных документов');
        }

        return response.json();
    }

    /**
     * Получить конкретный документ по ID
     */
    async getSignedDocument(documentId: number): Promise<SignedDocument> {
        const response = await fetch(`${API_BASE_URL}/signed-documents/${documentId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Документ не найден');
        }

        return response.json();
    }

    /**
     * Получить URL для скачивания документа
     */
    async getDocumentDownloadUrl(documentId: number): Promise<string> {
        const response = await fetch(`${API_BASE_URL}/signed-documents/${documentId}/download`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Ошибка получения ссылки для скачивания');
        }

        const data: DocumentDownloadResponse = await response.json();
        return data.downloadUrl;
    }

    /**
     * Скачать документ
     */
    async downloadDocument(documentId: number, fileName: string): Promise<void> {
        try {
            const downloadUrl = await this.getDocumentDownloadUrl(documentId);

            // Создаем невидимую ссылку для скачивания
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Ошибка скачивания документа:', error);
            throw error;
        }
    }

    /**
     * Скачать CMS подписи для договора (ZIP архив)
     */
    async downloadCmsZip(contractId: number, contractNumber: string): Promise<void> {
        try {
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/download-cms-zip`, {
                method: 'GET',
                headers: {
                    ...this.getAuthHeader(),
                },
            });

            if (!response.ok) {
                throw new Error('Ошибка скачивания CMS архива');
            }

            // Получаем blob данные
            const blob = await response.blob();

            // Создаем URL для blob
            const url = window.URL.createObjectURL(blob);

            // Создаем невидимую ссылку для скачивания
            const link = document.createElement('a');
            link.href = url;
            link.download = `CMS_signatures_${contractNumber}.zip`;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();

            // Очистка
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Ошибка скачивания CMS архива:', error);
            throw error;
        }
    }

    /**
     * Получить статистику документов компании
     */
    async getDocumentStatistics(): Promise<DocumentStatistics> {
        const response = await fetch(`${API_BASE_URL}/signed-documents/statistics`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Ошибка получения статистики документов');
        }

        return response.json();
    }

    /**
     * Получить доступные типы документов
     */
    async getDocumentTypes(): Promise<Record<string, string>> {
        const response = await fetch(`${API_BASE_URL}/signed-documents/types`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeader(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Ошибка получения типов документов');
        }

        const data: DocumentTypesResponse = await response.json();
        return data.types;
    }

    /**
     * Форматировать тип документа для отображения
     */
    getDocumentTypeLabel(type: SignedDocumentType): string {
        const typeLabels: Record<SignedDocumentType, string> = {
            [SignedDocumentType.CONTRACT]: 'Договор поставки',
            [SignedDocumentType.AVR]: 'АВР',
            [SignedDocumentType.PURCHASE_REQUEST]: 'Заявка на покупку',
        };

        return typeLabels[type] || type;
    }

    /**
     * Форматировать размер файла
     */
    formatFileSize(bytes?: number): string {
        if (!bytes) return 'Неизвестно';

        const sizes = ['Байт', 'КБ', 'МБ', 'ГБ'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));

        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    }

    /**
     * Форматировать дату
     */
    formatDate(dateString: string): string {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
}

export const signedDocumentService = new SignedDocumentService();
export default signedDocumentService;
